import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, UserRound, LayoutDashboard, Sparkles, Bell, Menu } from "lucide-react";
import { initials, avatarImgStyle } from "@/lib/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bandeau haut des pages de l'application (une fois connectée) :
// logo au centre · photo de profil + déconnexion à droite
export default function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [shopEnabled, setShopEnabled] = useState(false);
  const [hasAccomp, setHasAccomp] = useState(false);

  // Le bouton menu apparaît si la Boutique est activée OU si la personne a un accompagnement
  useEffect(() => {
    axios
      .get(`${API}/settings/public`)
      .then(({ data }) => setShopEnabled(!!data.settings?.shop_enabled))
      .catch(() => {});
  }, []);

  // « Mon accompagnement » n'apparaît que si Julie a attribué des pratiques à cette personne
  useEffect(() => {
    const token = localStorage.getItem("rav-token");
    if (!token) return;
    axios
      .get(`${API}/mon-accompagnement`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setHasAccomp(!!data.accompaniment))
      .catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("rav-token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API}/auth/me`, { headers })
      .then(({ data }) => setUser(data.user))
      .catch(() => {});
  }, []);

  // La pastille d'Instants et la cloche se rafraîchissent à chaque changement de page
  useEffect(() => {
    const token = localStorage.getItem("rav-token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API}/credits`, { headers })
      .then(({ data }) => setCredits(data))
      .catch(() => {});
    axios
      .get(`${API}/notifications`, { headers })
      .then(({ data }) => {
        setNotifs(data.notifications || []);
        setUnread(data.unread || 0);
      })
      .catch(() => {});
  }, [location.pathname]);

  // Pastille sur l'icône RAV de l'écran d'accueil (app installée, iOS 16.4+ / Android) :
  // le nombre de messages non lus s'affiche comme sur une app native
  useEffect(() => {
    if (!("setAppBadge" in navigator)) return;
    if (unread > 0) navigator.setAppBadge(unread).catch(() => {});
    else navigator.clearAppBadge?.().catch(() => {});
  }, [unread]);

  const openNotif = async (n) => {
    if (n.read) return;
    const token = localStorage.getItem("rav-token");
    setNotifs((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    axios.post(`${API}/notifications/${n.id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  const logout = async () => {
    const token = localStorage.getItem("rav-token");
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      /* ignore */
    }
    localStorage.removeItem("rav-token");
    localStorage.removeItem("rav-profile");
    navigate("/");
  };

  return (
    <>
      <div className="rav-topbar" data-testid="topbar">
        <div className="rav-topbar-side rav-topbar-left">
          {/* Menu tout à gauche, logo à sa droite, nom de marque — demande Julie */}
          <button
            type="button"
            className="rav-topbar-icon"
            data-testid="topbar-menu"
            onClick={() => {
              setShowNotifs(false);
              setShowMenu((v) => !v);
            }}
            aria-label="Menu"
            title="Menu"
          >
            <Menu size={19} strokeWidth={1.6} />
          </button>
          <button type="button" className="rav-topbar-logo rav-topbar-logo-sm" data-testid="topbar-logo" onClick={() => navigate("/accueil")} aria-label="Accueil">
            <img src="/logo-accueil.webp" alt="Respire Aime Vibre" draggable="false" />
          </button>
          <span className="rav-topbar-brand" data-testid="topbar-brand">Respire Aime Vibre</span>
        </div>
        <div className="rav-topbar-side rav-topbar-right">
          {/* Le menu est toujours visible : il donne accès aux Intervenants,
              à la Boutique (si activée) et à « Mon accompagnement » (si suivi) */}
          {(
            <>
              {showMenu && (
                <>
                <div className="rav-notif-panel rav-menu-panel" data-testid="menu-panel">
                  {hasAccomp && (
                    <>
                      <p className="rav-notif-panel-title">Votre suivi</p>
                      <button type="button" className="rav-notif-item" data-testid="menu-accompagnement" onClick={() => { setShowMenu(false); navigate("/mon-accompagnement"); }}>
                        <span className="rav-notif-item-name">Mon accompagnement</span>
                        <span className="rav-notif-item-text">Les pratiques choisies pour vous par Julie</span>
                      </button>
                    </>
                  )}
                  <p className="rav-notif-panel-title">Le lieu RAV</p>
                  <button type="button" className="rav-notif-item" data-testid="menu-intervenants" onClick={() => { setShowMenu(false); navigate("/intervenants"); }}>
                    <span className="rav-notif-item-name">Les intervenants</span>
                    <span className="rav-notif-item-text">Les visages qui font Respire Aime Vibre</span>
                  </button>
                  {shopEnabled && (
                    <>
                  <p className="rav-notif-panel-title">La Boutique</p>
                  <button type="button" className="rav-notif-item" data-testid="menu-ebooks" onClick={() => { setShowMenu(false); navigate("/boutique?tab=ebooks"); }}>
                    <span className="rav-notif-item-name">E-books</span>
                    <span className="rav-notif-item-text">Guides en PDF à télécharger</span>
                  </button>
                  <button type="button" className="rav-notif-item" data-testid="menu-programmes" onClick={() => { setShowMenu(false); navigate("/boutique?tab=programmes"); }}>
                    <span className="rav-notif-item-name">Programmes</span>
                    <span className="rav-notif-item-text">Parcours complets et thématiques</span>
                  </button>
                  <button type="button" className="rav-notif-item" data-testid="menu-livres" onClick={() => { setShowMenu(false); navigate("/boutique?tab=livres"); }}>
                    <span className="rav-notif-item-name">Livres</span>
                    <span className="rav-notif-item-text">Albums physiques, sur Amazon</span>
                  </button>
                    </>
                  )}
                </div>
                </>
              )}
            </>
          )}
          <button
            type="button"
            className="rav-topbar-icon rav-topbar-bell"
            data-testid="topbar-notifs"
            onClick={() => setShowNotifs((v) => !v)}
            aria-label="Notifications"
            title="Notifications"
          >
            <Bell size={19} strokeWidth={1.6} />
            {unread > 0 && <span className="rav-notif-badge" data-testid="topbar-notifs-badge">{unread}</span>}
          </button>
          {showNotifs && (
            <>
            <div className="rav-notif-panel" data-testid="notif-panel">
              <p className="rav-notif-panel-title">Messages de Julie</p>
              {notifs.length === 0 ? (
                <p className="rav-notif-empty" data-testid="notif-empty">Aucun message pour le moment.</p>
              ) : (
                notifs.map((n, i) => (
                  <button
                    key={n.id}
                    type="button"
                    className={`rav-notif-item${n.read ? "" : " is-unread"}`}
                    data-testid={`notif-item-${i}`}
                    onClick={() => openNotif(n)}
                  >
                    <span className="rav-notif-item-name">{n.name}</span>
                    <span className="rav-notif-item-text">{n.text}</span>
                    <span className="rav-notif-item-date">
                      {n.created_at ? new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : ""}
                    </span>
                  </button>
                ))
              )}
            </div>
            </>
          )}
          {credits && !credits.unlimited && (
            <span
              className="rav-topbar-credits"
              data-testid="topbar-credits"
              title={`Il vous reste ${credits.remaining} Instant${credits.remaining > 1 ? "s" : ""} à consommer`}
            >
              <Sparkles size={14} strokeWidth={1.6} />
              {credits.remaining}
            </span>
          )}
          {user?.role === "superadmin" && (
            <button
              type="button"
              className="rav-topbar-icon"
              data-testid="topbar-admin"
              onClick={() => navigate("/admin")}
              aria-label="Espace Admin"
              title="Espace Admin"
            >
              <LayoutDashboard size={19} strokeWidth={1.6} />
            </button>
          )}
          <button
            type="button"
            className="rav-topbar-avatar"
            data-testid="topbar-avatar"
            onClick={() => navigate("/profil")}
            aria-label="Mon profil"
          >
            {user?.photo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${user.photo_url}`} style={avatarImgStyle(user)} alt={user.name || "Profil"} />
            ) : user && initials(user.name, user.last_name) ? (
              <span className="rav-avatar-initials rav-avatar-initials-sm">{initials(user.name, user.last_name)}</span>
            ) : (
              <UserRound size={19} strokeWidth={1.6} />
            )}
          </button>
          <button
            type="button"
            className="rav-topbar-icon"
            data-testid="topbar-logout"
            onClick={logout}
            aria-label="Se déconnecter"
            title="Se déconnecter"
          >
            <LogOut size={19} strokeWidth={1.6} />
          </button>
        </div>
      </div>
      {/* Voile plein écran : ferme le menu ou les notifications au toucher ailleurs.
          Placé HORS de la barre (sa transformation CSS piégerait le voile dans sa zone). */}
      {(showMenu || showNotifs) && (
        <div
          className="rav-panel-overlay"
          data-testid="panel-overlay"
          onClick={() => { setShowMenu(false); setShowNotifs(false); }}
        />
      )}
      <div className="rav-topbar-spacer" aria-hidden="true" />
    </>
  );
}
